import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BsArrowLeft, BsAirplane, BsTruck } from "react-icons/bs";
import { FaShip } from "react-icons/fa";
import axiosClient from "../../api/api";
import Line from "../../components/InputComponents/Line";
import { PrimaryContext } from "../../components/MainContext/MainContext";
import ChinaContent from "./ChinaContent";
import GenericContent from "./GenericContent";
import georgianCities from "../../data/georgianCities";
import {
  HIGHLAND_CITIES,
  currencySymbol,
  normaliseBrackets,
} from "../../data/deliveryTariffs";
// HomeDeliveryPriceTable used only on the landing-page calculator, not here.

// Turkey (country id=4) was discontinued 2026-08 — the picker filters those
// warehouses out. Any historical Turkey pack still ships via the standard
// GenericContent path.
const HIDDEN_COUNTRY_IDS = new Set([4]);

// Human labels for abroad_office.type. Matches the shipping mode conventions used
// across the rest of the app (see PanelFlights, admin panel).
const typeMeta = {
  1: { icon: BsTruck,   labelKey: "land", fallback: "სახმელეთო" },
  2: { icon: BsAirplane, labelKey: "air",  fallback: "საავიაციო" },
  3: { icon: FaShip,    labelKey: "sea",  fallback: "საზღვაო" },
};

/**
 * Two-step address browser:
 *   1) Country picker — large image cards per available country.
 *   2) After a country is picked, the abroad-office details for that country
 *      render below and a "back" button returns to the picker.
 * `/countries` and `/abroadOffice` are public endpoints that already filter
 * hidden warehouses, so we only render whatever the server hands back.
 */
const Addresses = () => {
  const { t } = useTranslation();
  const { user } = useContext(PrimaryContext);

  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null); // {id, name, img}
  const [addresses, setAddresses] = useState([]);
  const [selectedType, setSelectedType] = useState(null); // shipping-mode filter
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [loadingAddresses, setLoadingAddresses] = useState(false);

  // Which shipping modes actually exist for the currently selected country
  // (deduped so the switcher only shows real options).
  const availableTypes = useMemo(
    () => [...new Set(addresses.map((a) => a.type))].sort(),
    [addresses]
  );
  // Filtered address list according to the current type filter (or all if none).
  const visibleAddresses = useMemo(
    () =>
      selectedType == null
        ? addresses
        : addresses.filter((a) => a.type === selectedType),
    [addresses, selectedType]
  );

  useEffect(() => {
    setLoadingCountries(true);
    axiosClient
      .get("/countries")
      .then(({ data }) =>
        setCountries(
          (data || []).filter((c) => !HIDDEN_COUNTRY_IDS.has(Number(c.id)))
        )
      )
      .finally(() => setLoadingCountries(false));
  }, []);

  useEffect(() => {
    if (!selectedCountry) return;
    setLoadingAddresses(true);
    setSelectedType(null);
    axiosClient
      .get(`/abroadOffice?country_id=${selectedCountry.id}`)
      .then(({ data }) => {
        setAddresses(data);
        // If the country has more than one shipping mode (China has air / sea /
        // land), preselect the first one so we don't dump 3 warehouse cards on
        // the user. Single-mode countries stay unfiltered.
        const uniqTypes = [...new Set(data.map((a) => a.type))];
        if (uniqTypes.length > 1) setSelectedType(uniqTypes.sort()[0]);
      })
      .finally(() => setLoadingAddresses(false));
  }, [selectedCountry]);

  const flagUrl = (img) =>
    img ? `${process.env.REACT_APP_API_URL}/storage/${img}` : null;

  return (
    <div>
      <div className="flex flex-col gap-2 sm:w-full sm:justify-between md:items-center">
        <Line className="bg-teal_600 h-1 w-10" />
        <h2 className="font-semibold text-2xl md:text-xl">{t("addresses")}</h2>
      </div>

      {/* Step 1: country picker */}
      {!selectedCountry ? (
        <div className="mt-6">
          <p className="text-sm text-gray-500 mb-4">
            {t("pick_country_for_address") ||
              "აირჩიე ქვეყანა, რომ ნახო შესაბამისი მისამართი"}
          </p>

          {loadingCountries ? (
            <div className="grid grid-cols-3 md:grid-cols-2 sm:grid-cols-2 gap-5">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="animate-pulse bg-gray-200 rounded-3xl aspect-[4/3]"
                />
              ))}
            </div>
          ) : countries.length === 0 ? (
            <div className="h-[200px] flex items-center justify-center bg-white rounded-3xl">
              <p className="font-bold text-gray-500">{t("empty") || "empty"}</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 md:grid-cols-2 sm:grid-cols-2 gap-5">
              {countries.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setSelectedCountry(c)}
                  className="group relative overflow-hidden rounded-3xl bg-white shadow-bs1
                             aspect-[4/3] flex flex-col items-center justify-center gap-3
                             transition-all hover:shadow-lg hover:-translate-y-1
                             border border-transparent hover:border-teal_600"
                >
                  {flagUrl(c.img) ? (
                    <img
                      src={flagUrl(c.img)}
                      alt={c.name}
                      className="h-2/3 max-h-40 object-contain
                                 transition-transform group-hover:scale-105"
                    />
                  ) : (
                    <div className="h-2/3 max-h-40 w-2/3 bg-gray-100 rounded-2xl
                                    flex items-center justify-center text-gray-400 text-4xl font-bold">
                      {c.name?.[0] ?? "?"}
                    </div>
                  )}
                  <p className="font-bold text-lg sm:text-base text-slate-700">
                    {t(c.name) || c.name}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        /* Step 2: addresses for the picked country */
        <div className="mt-6">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={() => {
                setSelectedCountry(null);
                setAddresses([]);
              }}
              className="flex items-center gap-2 rounded-full bg-white shadow-bs1
                         px-4 py-2 text-sm font-bold text-slate-700 hover:bg-gray-50"
            >
              <BsArrowLeft />
              {t("back_to_countries") || "უკან"}
            </button>
            <div className="flex items-center gap-2">
              {flagUrl(selectedCountry.img) && (
                <img
                  src={flagUrl(selectedCountry.img)}
                  alt={selectedCountry.name}
                  className="h-8 w-auto object-contain"
                />
              )}
              <p className="font-bold text-lg sm:text-base">
                {t(selectedCountry.name) || selectedCountry.name}
              </p>
            </div>
          </div>

          {/* Shipping-mode switcher — only appears when the country has more
              than one warehouse type (e.g. China: air / sea / land). */}
          {availableTypes.length > 1 && (
            <div className="flex gap-2 flex-wrap mb-4">
              {availableTypes.map((tp) => {
                const meta = typeMeta[tp];
                if (!meta) return null;
                const Icon = meta.icon;
                const active = selectedType === tp;
                return (
                  <button
                    key={tp}
                    onClick={() => setSelectedType(tp)}
                    className={`flex items-center gap-2 rounded-full px-5 py-2
                      text-sm font-bold transition-all
                      ${
                        active
                          ? "bg-teal_600 text-white shadow-bs1"
                          : "bg-white text-slate-600 hover:bg-gray-50 border border-gray-200"
                      }`}
                  >
                    <Icon className="text-lg" />
                    {t(meta.labelKey) || meta.fallback}
                  </button>
                );
              })}
            </div>
          )}

          {loadingAddresses ? (
            <div className="grid grid-cols-1 gap-3">
              {[0, 1].map((i) => (
                <div
                  key={i}
                  className="animate-pulse bg-gray-200 w-full h-60 rounded-3xl"
                />
              ))}
            </div>
          ) : addresses.length === 0 ? (
            <div className="h-[160px] flex items-center justify-center bg-white rounded-3xl">
              <p className="font-bold text-gray-500">
                {t("no_office_in_country") ||
                  "ამ ქვეყანაში საწყობი ჯერ არ არის"}
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {visibleAddresses.map((item) => (
                <AddressCard
                  key={item.id}
                  item={item}
                  user={user}
                  selectedCountry={selectedCountry}
                  t={t}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * One warehouse row. Held its own component so we can give the pricing table
 * a ref and let GenericContent scroll to it via a callback.
 */
const AddressCard = ({ item, user, selectedCountry, t }) => {
  return (
    <div className="shadow-bs1 rounded-3xl sm:rounded-lg bg-white">
      {selectedCountry.id === 3 && (
        <ChinaContent
          item={item}
          user={user}
          currentCountryName={selectedCountry.name}
          currentCountry={selectedCountry.id}
        />
      )}
      {selectedCountry.id !== 3 && (
        <GenericContent
          item={item}
          user={user}
          currentCountryName={selectedCountry.name}
        />
      )}
      {item.home_delivery_only && (() => {
        // Read the tariff from the office's brackets[] (new shape) so the
        // marketing lines stay accurate as admin edits rates. Both per-kg
        // rates come from the FIRST bracket (≤ included_kg range) — that's
        // what the operator wants advertised in the card copy.
        const brs = normaliseBrackets(item.delivery_tariff);
        const first = brs[0];
        const perKgTbilisi = first ? Number(first.transport_per_kg) : null;
        const perKgHighland = first
          ? Number(
              first.transport_per_kg_highland ?? first.transport_per_kg
            )
          : null;
        const sym = currencySymbol(item.currency);
        const includedTo = first ? Number(first.to_kg) : 10;
        return (
          <div className="p-4 border-t border-gray-200 flex flex-col gap-2">
            <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
              📦{" "}
              {t("home_delivery_only_notice") ||
                "ამ ქვეყნიდან ამანათი მხოლოდ თქვენს მისამართზე მიდის — ოფისზე გატანა არ ხდება"}
            </p>
            {perKgTbilisi != null && (
              <div className="text-xs text-teal-800 bg-teal-50 rounded-lg px-3 py-2 font-semibold leading-snug">
                <div className="mb-1">
                  ✅ {includedTo} კგ-მდე ამანათების ტრანსპორტირება + მიტანა:
                </div>
                <div className="flex flex-col gap-1 pl-4 text-slate-700 font-normal">
                  <div>
                    <b>{t("tbilisi_and_regions") || "თბილისი / რეგიონები"}:</b>{" "}
                    <b className="text-teal-800">
                      {perKgTbilisi.toFixed(2)} {sym} / კგ
                    </b>
                  </div>
                  <div>
                    <b>{t("highland") || "მაღალმთიანი"}:</b>{" "}
                    <b className="text-teal-800">
                      {perKgHighland.toFixed(2)} {sym} / კგ
                    </b>{" "}
                  </div>
                </div>
                <div className="mt-2 text-slate-700 font-normal">
                  {includedTo} კგ-ის ზემოთ ემატება მიტანის თანხა ლარში. სრული
                  ცხრილი მთავარ გვერდზე კალკულატორის საშუალებით.
                </div>
              </div>
            )}
            <p className="text-[11px] text-slate-500 leading-snug">
              <span className="font-semibold">
                {t("highland") || "მაღალმთიანი"}
              </span>{" "}
              —{" "}
              {georgianCities
                .filter((c) => HIGHLAND_CITIES.has(c.value))
                .map((c) => c.label)
                .join(", ")}
              .{" "}
              {t("other_cities_use_tbilisi_rate") ||
                "დანარჩენ ქალაქებზე გამოიყენება „თბილისი / რეგიონები“-ს ტარიფი."}
            </p>
          </div>
        );
      })()}

      {/* China (standard-pipeline, not home_delivery_only) — same descriptive
          block as USA/Greece but sourced from the office's scheduled overlay
          (price_scheduled + price_scheduled_highland). Renders whenever the
          office carries the overlay pair; a "from DD.MM.YYYY" chip appears
          while the effective date is still in the future. */}
      {selectedCountry.id === 3 &&
        item.price_scheduled != null &&
        item.price_scheduled_highland != null &&
        (() => {
          const perKgTbilisi = Number(item.price_scheduled);
          const perKgHighland = Number(item.price_scheduled_highland);
          const sym = "$";
          const scheduledFrom = item.scheduled_from
            ? new Date(item.scheduled_from)
            : null;
          const scheduledInFuture = scheduledFrom
            ? scheduledFrom.setHours(0, 0, 0, 0) >
              new Date().setHours(0, 0, 0, 0)
            : false;
          const formatDay = (d) => {
            if (!d) return "";
            const dt = new Date(d);
            return `${String(dt.getDate()).padStart(2, "0")}.${String(
              dt.getMonth() + 1
            ).padStart(2, "0")}.${dt.getFullYear()}`;
          };
          return (
            <div className="p-4 border-t border-gray-200 flex flex-col gap-2">
              <div className="text-xs text-teal-800 bg-teal-50 rounded-lg px-3 py-2 font-semibold leading-snug">
                <div className="mb-1 flex items-baseline gap-2 flex-wrap">
                  ✅ ტრანსპორტირების ტარიფი:
                  {scheduledInFuture && (
                    <span className="text-teal_600 text-[10px] font-bold uppercase tracking-wider">
                      {formatDay(item.scheduled_from)} დან
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-1 pl-4 text-slate-700 font-normal">
                  <div>
                    <b>{t("tbilisi_and_regions") || "თბილისი / რეგიონები"}:</b>{" "}
                    <b className="text-teal-800">
                      {perKgTbilisi.toFixed(2)} {sym} / კგ
                    </b>
                  </div>
                  <div>
                    <b>{t("highland") || "მაღალმთიანი"}:</b>{" "}
                    <b className="text-teal-800">
                      {perKgHighland.toFixed(2)} {sym} / კგ
                    </b>{" "}
                    ჯდება.
                  </div>
                </div>
                <div className="mt-2 text-slate-700 font-normal">
                  ტრანსპორტირების ღირებულება გამოითვლება ამანათის საერთო
                  წონისა და მისამართის მიხედვით. სრული კალკულაცია მთავარ
                  გვერდზე კალკულატორის საშუალებით.
                </div>
              </div>
              <p className="text-[11px] text-slate-500 leading-snug">
                <span className="font-semibold">
                  {t("highland") || "მაღალმთიანი"}
                </span>{" "}
                —{" "}
                {georgianCities
                  .filter((c) => HIGHLAND_CITIES.has(c.value))
                  .map((c) => c.label)
                  .join(", ")}
                .{" "}
                {t("other_cities_use_tbilisi_rate") ||
                  "დანარჩენ ქალაქებზე გამოიყენება „თბილისი / რეგიონები“-ს ტარიფი."}
              </p>
            </div>
          );
        })()}
    </div>
  );
};

export default Addresses;
